/**
 * IraContributionLimitsPage — Reference · IRA Contribution Limits.
 *
 * IRS-published annual IRA contribution + catch-up limits, keyed by tax year
 * and plan code. The IRA wizard reads this to enforce caps and to print the
 * right figure in the contribution disclosure. Read-mostly: an admin updates
 * it each fall when the IRS posts the next tax year's limits (Pub 590-A).
 *
 * Contract (verified against the backend 2026-06-16 — entity / read route /
 * upsert DTO / service all agree on field names; NO drift found):
 *   - Read:  GET /v3/config/ira-config → { contributionLimits, planPrefixes, … }
 *            V3ConfigFunction.IraConfig returns the raw IraContributionLimit
 *            entities, so the wire fields are the camelCased entity props:
 *            taxYear, planCode, regularLimit, catchUpLimit, catchUpAgeThreshold,
 *            notes, isActive (+ createdAt/createdBy ignored here). These match
 *            the Row interface 1:1 — decimals (RegularLimit/CatchUpLimit) and
 *            the byte CatchUpAgeThreshold all serialize as JSON numbers.
 *   - Write: client.retail.admin.iraContributionLimits.upsert(token, body)
 *            → POST /v3/admin/ira-contribution-limits. The DTO
 *            (IraContributionLimitUpsertInput) carries every field and
 *            AdminSystemService.UpsertIraContributionLimitAsync persists each
 *            one on both create AND update — no silent drop. Composite key
 *            (taxYear, planCode); planCode is upper-cased server-side.
 *   - Soft-delete: re-upsert with isActive=false (the entity has IsActive, and
 *            existing IRAs reference historical limits in their disclosures, so
 *            we keep the row for audit rather than hard-DELETE).
 *
 * Migrated 2026-06-16 from ROP-teams Admin (operational app writes no config).
 * Auth: ADMIN_AUTH_TOKEN_SENTINEL (MSAL bearer; no secret in the bundle) —
 * passed as the typed upsert's first (bypassKey) arg.
 */

import { useCallback, useEffect, useMemo, useState } from 'react';
import { ADMIN_AUTH_TOKEN_SENTINEL, ApiError } from '@retail-ops/api-client';
import { useApiClient } from '@/contexts/ApiClientContext';
import { Banner, Button, Card, Pill, Spinner } from '@/components/primitives';
import { DataTable, type DataTableColumn } from '@/components/DataTable';
import { TextArea, NumberField, SelectField, BoolField } from '@/components/FormField';
import { ModalShell } from '@/components/ModalShell';

interface LimitRow {
  taxYear: number;
  planCode: string;
  regularLimit: number;
  catchUpLimit: number;
  catchUpAgeThreshold: number;
  notes: string | null;
  isActive: boolean;
}

interface PlanPrefixRow {
  planCode: string;
  planType: string;
  prefix: string;
  isActive: boolean;
}

interface EditDraft extends LimitRow {
  isNew: boolean;
}

function emptyDraft(defaultPlanCode: string): EditDraft {
  return {
    taxYear: new Date().getFullYear(),
    planCode: defaultPlanCode,
    regularLimit: 0,
    catchUpLimit: 0,
    catchUpAgeThreshold: 50,
    notes: '',
    isActive: true,
    isNew: true,
  };
}

function fmtUsd(n: number): string {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    maximumFractionDigits: 0,
  }).format(n);
}

export function IraContributionLimitsPage(): JSX.Element {
  const client = useApiClient();
  const [rows, setRows] = useState<ReadonlyArray<LimitRow> | null>(null);
  const [planPrefixes, setPlanPrefixes] = useState<ReadonlyArray<PlanPrefixRow>>([]);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [yearFilter, setYearFilter] = useState<string>('all');
  const [draft, setDraft] = useState<EditDraft | null>(null);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<LimitRow | null>(null);
  const [toast, setToast] = useState<{ tone: 'info' | 'warn'; text: string } | null>(null);
  const [refreshNonce, setRefreshNonce] = useState(0);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      setRows(null);
      setLoadError(null);
      try {
        // Use the raw GET — the typed config.iraConfig() is mis-declared as an
        // array, but the route returns an object envelope. Field names are the
        // verified camelCased entity props (see doc-comment header).
        const resp = await client.http.get<{
          success: boolean;
          data?: {
            contributionLimits: ReadonlyArray<LimitRow>;
            planPrefixes: ReadonlyArray<PlanPrefixRow>;
          };
          error?: string;
        }>('/v3/config/ira-config');
        if (cancelled) return;
        if (resp.success && resp.data) {
          setRows(resp.data.contributionLimits ?? []);
          setPlanPrefixes(resp.data.planPrefixes ?? []);
        } else {
          setLoadError(resp.error ?? 'Failed to load IRA contribution limits.');
        }
      } catch (e) {
        if (cancelled) return;
        setLoadError(ApiError.isApiError(e) ? e.message : e instanceof Error ? e.message : String(e));
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [client, refreshNonce]);

  const refresh = useCallback(() => setRefreshNonce((n) => n + 1), []);

  const planByCode = useMemo(() => {
    const m = new Map<string, PlanPrefixRow>();
    for (const p of planPrefixes) m.set(p.planCode, p);
    return m;
  }, [planPrefixes]);

  const planOptions = useMemo(
    () => planPrefixes.map((p) => ({ value: p.planCode, label: `${p.planCode} · ${p.planType}` })),
    [planPrefixes],
  );

  const yearsAvailable = useMemo(() => {
    const set = new Set<number>((rows ?? []).map((r) => r.taxYear));
    return Array.from(set).sort((a, b) => b - a);
  }, [rows]);

  const yearOptions = useMemo(
    () => [
      { value: 'all', label: 'All years' },
      ...yearsAvailable.map((y) => ({ value: String(y), label: String(y) })),
    ],
    [yearsAvailable],
  );

  const visible = useMemo(() => {
    const all = (rows ?? []).slice().sort((a, b) => {
      if (a.taxYear !== b.taxYear) return b.taxYear - a.taxYear;
      return a.planCode.localeCompare(b.planCode);
    });
    if (yearFilter === 'all') return all;
    return all.filter((r) => String(r.taxYear) === yearFilter);
  }, [rows, yearFilter]);

  const upsert = useCallback(
    async (row: LimitRow): Promise<boolean> => {
      const resp = await client.retail.admin.iraContributionLimits.upsert(ADMIN_AUTH_TOKEN_SENTINEL, {
        taxYear: row.taxYear,
        planCode: row.planCode.trim(),
        regularLimit: row.regularLimit,
        catchUpLimit: row.catchUpLimit,
        catchUpAgeThreshold: row.catchUpAgeThreshold,
        notes: row.notes,
        isActive: row.isActive,
      });
      if (!resp.success) throw new Error(resp.error ?? 'admin upsert returned success=false');
      return true;
    },
    [client],
  );

  const handleSave = useCallback(async () => {
    if (!draft) return;
    if (!draft.planCode.trim()) {
      setSaveError('Pick a plan code.');
      return;
    }
    if (draft.taxYear < 1980 || draft.taxYear > 2100) {
      setSaveError('Pick a sensible tax year (1980–2100).');
      return;
    }
    if (draft.regularLimit < 0 || draft.catchUpLimit < 0) {
      setSaveError('Limits must be 0 or higher.');
      return;
    }
    setSaving(true);
    setSaveError(null);
    try {
      await upsert(draft);
      setDraft(null);
      setToast({ tone: 'info', text: `Saved. ${draft.taxYear} ${draft.planCode} limits updated.` });
      refresh();
    } catch (e) {
      setSaveError(ApiError.isApiError(e) ? e.message : e instanceof Error ? e.message : String(e));
    } finally {
      setSaving(false);
    }
  }, [draft, upsert, refresh]);

  const handleSoftDelete = useCallback(async () => {
    if (!deleteTarget) return;
    setSaving(true);
    setSaveError(null);
    try {
      // Soft-disable: existing IRAs already opened reference historical limits
      // in their disclosures. Mark inactive rather than hard-deleting.
      await upsert({ ...deleteTarget, isActive: false });
      setToast({
        tone: 'warn',
        text: `${deleteTarget.taxYear} ${deleteTarget.planCode} is now inactive. The IRA wizard will skip it.`,
      });
      setDeleteTarget(null);
      refresh();
    } catch (e) {
      setSaveError(ApiError.isApiError(e) ? e.message : e instanceof Error ? e.message : String(e));
    } finally {
      setSaving(false);
    }
  }, [deleteTarget, upsert, refresh]);

  const columns: ReadonlyArray<DataTableColumn<LimitRow>> = useMemo(
    () => [
      { header: 'Tax year', cell: (r) => <code className="cell-mono--bold">{r.taxYear}</code> },
      { header: 'Plan', cell: (r) => <code className="cell-mono">{r.planCode}</code> },
      {
        header: 'Plan type',
        cell: (r) => <span className="cell-desc">{planByCode.get(r.planCode)?.planType ?? '—'}</span>,
      },
      {
        header: 'Regular limit',
        align: 'right',
        cell: (r) => <span className="num cell-strong">{fmtUsd(r.regularLimit)}</span>,
      },
      {
        header: 'Catch-up',
        align: 'right',
        cell: (r) => <span className="num">{fmtUsd(r.catchUpLimit)}</span>,
      },
      {
        header: 'Catch-up age',
        align: 'right',
        cell: (r) => <span className="num cell-desc">{r.catchUpAgeThreshold}+</span>,
      },
      {
        header: 'Active',
        cell: (r) => (r.isActive ? <Pill tone="ok">active</Pill> : <Pill tone="muted">inactive</Pill>),
      },
      {
        header: '',
        cell: (r) => (
          <div className="card-actions-row">
            <Button onClick={() => { setDraft({ ...r, isNew: false }); setSaveError(null); }}>Edit</Button>
            {r.isActive && <Button onClick={() => setDeleteTarget(r)}>Deactivate</Button>}
          </div>
        ),
      },
    ],
    [planByCode],
  );

  if (loadError) {
    return (
      <Banner tone="err">
        Could not load IRA contribution limits: {loadError} <Button onClick={refresh}>Retry</Button>
      </Banner>
    );
  }
  if (!rows) return <Spinner label="Loading IRA contribution limits…" />;

  return (
    <>
      {toast && (
        <Banner tone={toast.tone}>
          {toast.text} <Button onClick={() => setToast(null)}>Dismiss</Button>
        </Banner>
      )}

      <Banner tone="warn">
        Confirm new figures against <strong>IRS Publication 590-A</strong> before saving. A wrong
        number here can over-fund a customer&apos;s IRA and trigger excise penalties.
      </Banner>

      <Card
        title={`IRA Contribution Limits — ${visible.length} of ${rows.length}`}
        actions={
          <div className="card-actions-row">
            <div className="users-filter">
              <SelectField
                label=""
                value={yearFilter}
                onChange={setYearFilter}
                options={yearOptions}
              />
            </div>
            <Button
              variant="primary"
              disabled={planPrefixes.length === 0}
              onClick={() => { setDraft(emptyDraft(planPrefixes[0]?.planCode ?? '')); setSaveError(null); }}
            >
              + Add Year/Plan
            </Button>
          </div>
        }
      >
        <p className="cell-desc">
          Annual IRS contribution caps and catch-up amounts per IRA plan type. The IRA wizard reads
          this to enforce caps and to print the right limit in the contribution disclosure. Update
          each fall when the IRS posts the next tax year&apos;s limits.
        </p>
        <DataTable<LimitRow>
          columns={columns}
          rows={visible}
          rowKey={(r) => `${r.taxYear}|${r.planCode}`}
          emptyState={<p className="empty">No contribution limits for the selected year.</p>}
        />
      </Card>

      <ModalShell
        open={draft !== null}
        title={draft?.isNew ? 'Add a year/plan limit' : `Edit ${draft?.taxYear ?? ''} ${draft?.planCode ?? ''}`}
        onClose={() => {
          if (saving) return;
          setDraft(null);
          setSaveError(null);
        }}
        width={680}
        actions={
          draft && (
            <>
              <Button onClick={() => setDraft(null)} disabled={saving}>
                Cancel
              </Button>
              <Button variant="primary" onClick={() => void handleSave()} disabled={saving}>
                {saving ? 'Saving…' : 'Save'}
              </Button>
            </>
          )
        }
      >
        {draft && (
          <div className="form-stack">
            {saveError && <Banner tone="err">{saveError}</Banner>}

            <div className="form-row">
              <NumberField
                label="Tax year"
                required
                value={draft.taxYear}
                onChange={(v) => draft.isNew && setDraft({ ...draft, taxYear: v ?? draft.taxYear })}
                min={1980}
                max={2100}
                disabled={!draft.isNew}
                placeholder="2026"
                hint={draft.isNew ? 'IRS tax year these limits apply to.' : 'Permanent — set at creation.'}
              />
              <SelectField
                label="Plan code"
                required
                value={draft.planCode}
                onChange={(v) => draft.isNew && setDraft({ ...draft, planCode: v })}
                options={planOptions}
                placeholder="— Select plan —"
                disabled={!draft.isNew}
                hint={draft.isNew ? 'Which IRA plan type this row applies to.' : 'Permanent.'}
              />
            </div>

            <div className="form-row">
              <NumberField
                label="Regular limit (USD)"
                required
                value={draft.regularLimit}
                onChange={(v) => setDraft({ ...draft, regularLimit: v ?? 0 })}
                min={0}
                step={500}
                placeholder="7000"
              />
              <NumberField
                label="Catch-up limit (USD)"
                value={draft.catchUpLimit}
                onChange={(v) => setDraft({ ...draft, catchUpLimit: v ?? 0 })}
                min={0}
                step={500}
                placeholder="1000"
                hint="Additional amount allowed for savers at/above the catch-up age."
              />
            </div>

            <div className="form-row">
              <NumberField
                label="Catch-up age threshold"
                value={draft.catchUpAgeThreshold}
                onChange={(v) =>
                  setDraft({ ...draft, catchUpAgeThreshold: Math.max(0, Math.min(120, v ?? 50)) })
                }
                min={0}
                max={120}
                placeholder="50"
                hint="Age at which a saver becomes eligible for the catch-up. IRS standard is 50."
              />
              <BoolField
                label="Active"
                value={draft.isActive}
                onChange={(v) => setDraft({ ...draft, isActive: v })}
                hint="Inactive limits are skipped by the IRA wizard."
              />
            </div>

            <TextArea
              label="Notes"
              value={draft.notes ?? ''}
              onChange={(v) => setDraft({ ...draft, notes: v || null })}
              rows={2}
              placeholder="e.g. IRS Pub 590-A revised Oct 2025; SECURE 2.0 Section 109"
            />

            <Banner tone="info">
              <strong>Plain-English:</strong> For tax year <strong>{draft.taxYear}</strong>, a{' '}
              <strong>{draft.planCode || '—'}</strong> plan accepts up to{' '}
              <strong>{fmtUsd(draft.regularLimit)}</strong> regular plus{' '}
              <strong>{fmtUsd(draft.catchUpLimit)}</strong> catch-up for savers age{' '}
              <strong>{draft.catchUpAgeThreshold}+</strong>.
            </Banner>
          </div>
        )}
      </ModalShell>

      <ModalShell
        open={deleteTarget !== null}
        title="Mark this limit inactive?"
        onClose={() => {
          if (saving) return;
          setDeleteTarget(null);
          setSaveError(null);
        }}
        width={520}
        actions={
          deleteTarget && (
            <>
              <Button onClick={() => setDeleteTarget(null)} disabled={saving}>
                Keep it
              </Button>
              <Button variant="primary" onClick={() => void handleSoftDelete()} disabled={saving}>
                {saving ? 'Working…' : 'Mark inactive'}
              </Button>
            </>
          )
        }
      >
        {deleteTarget && (
          <div className="form-stack">
            {saveError && <Banner tone="err">{saveError}</Banner>}
            <p className="modal-prose">
              We&apos;ll set{' '}
              <strong>
                {deleteTarget.taxYear} {deleteTarget.planCode}
              </strong>{' '}
              to inactive. New IRA enrollments won&apos;t see this limit, but the row stays for audit —
              existing IRAs already opened reference historical limits in their disclosures. Flip it
              back on anytime.
            </p>
          </div>
        )}
      </ModalShell>
    </>
  );
}

export default IraContributionLimitsPage;
